/**
 * Validates and sanitizes a redirect URL to prevent open redirect vulnerabilities
 * Only allows same-origin absolute paths (starting with /)
 *
 * @param redirectUrl - The redirect URL to validate
 * @returns The validated URL if safe, null otherwise
 */
export const validateRedirectUrl = (redirectUrl: string | null): string | null => {
  if (!redirectUrl) {
    return null;
  }

  try {
    const decodedUrl = decodeURIComponent(redirectUrl);

    // Security: Only allow same-origin absolute paths
    // Reject protocol-relative URLs (//evil.com), external URLs (http://, https://),
    // javascript: URLs, data: URLs, and any other non-absolute-path schemes
    if (
      decodedUrl.startsWith("/") &&
      !decodedUrl.startsWith("//") && // Prevent protocol-relative URLs
      !decodedUrl.includes("\\") && // Browsers read "\" as "/", so "/\evil.com" is "//evil.com"
      // Browsers drop tabs and newlines, so "/\t/evil.com" would also become "//evil.com"
      !/[\u0000-\u001f\u007f]/.test(decodedUrl) &&
      !decodedUrl.match(/^\/[a-zA-Z]:/) && // Prevent Windows paths like /C:
      !decodedUrl.includes("://") && // Prevent any protocol
      !decodedUrl.includes("javascript:") && // Prevent XSS
      !decodedUrl.includes("data:") && // Prevent data URLs
      !decodedUrl.includes("<") && // Prevent HTML injection attempts
      !decodedUrl.includes(">") // Prevent HTML injection attempts
    ) {
      return decodedUrl;
    }

    return null;
  } catch {
    // Invalid URL encoding, reject
    return null;
  }
};

/**
 * Safely extracts and validates redirect parameter from URL search params
 *
 * @param searchParams - URLSearchParams object or search string
 * @returns Validated redirect URL or null
 */
export const getValidatedRedirectUrl = (searchParams: URLSearchParams | string): string | null => {
  const params =
    typeof searchParams === "string" ? new URLSearchParams(searchParams) : searchParams;

  const redirectUrl = params.get("redirect");
  return validateRedirectUrl(redirectUrl);
};

/** Where to go after signing in: the validated `?redirect=` path, else `fallback`. */
export const resolvePostLoginPath = (search: string, fallback: string): string =>
  getValidatedRedirectUrl(search) ?? fallback;

/**
 * Login URL that brings the user back to `pathname + search` after signing in.
 * The app root needs no return path, so it gets the plain login URL.
 */
export const buildLoginRedirectPath = (
  loginPath: string,
  location: { pathname: string; search?: string }
): string => {
  const target = `${location.pathname}${location.search ?? ""}`;
  if (location.pathname === "/" && !location.search) {
    return loginPath;
  }
  if (!validateRedirectUrl(encodeURIComponent(target))) {
    return loginPath;
  }
  return `${loginPath}?redirect=${encodeURIComponent(target)}`;
};

const OAUTH_REDIRECT_STORAGE_KEY = "oauthRedirect";

/** Keeps the validated return path across the OAuth round trip (same tab, so sessionStorage). */
export const oauthRedirectStorage = {
  save(search: string): void {
    try {
      const redirectUrl = getValidatedRedirectUrl(search);
      if (redirectUrl) {
        sessionStorage.setItem(OAUTH_REDIRECT_STORAGE_KEY, redirectUrl);
      } else {
        sessionStorage.removeItem(OAUTH_REDIRECT_STORAGE_KEY);
      }
    } catch {
      /* storage unavailable: the user lands on the dashboard */
    }
  },
  /** Returns the saved path (validated again) and clears it. */
  consume(): string | null {
    try {
      const stored = sessionStorage.getItem(OAUTH_REDIRECT_STORAGE_KEY);
      sessionStorage.removeItem(OAUTH_REDIRECT_STORAGE_KEY);
      return stored ? validateRedirectUrl(encodeURIComponent(stored)) : null;
    } catch {
      return null;
    }
  },
};
